'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { createEventBus } from '@/shared/utils';
import {
  TeacherEventKeys,
  TeacherEvents,
  TeacherItem,
  TeacherList,
  TeacherScreen,
} from '@/teacher';
import { Teacher as TeacherDict } from '@/shared/i18n';
import Grid from '@mui/material/Grid2';
import { Card, Skeleton } from '@mui/material';
import { Course } from 'src/modules/teacher/types';
import { useProfileStore } from '@/profile';
import { routes } from 'src/shared/utils/routes';

interface TeachersContainerProps {
  dict: TeacherDict;
  initialData: { teacher: TeacherItem | TeacherList; topVideos?: Course[] } | null; // Aggiunto `null`
  type: 'detail' | 'list';
}

function TeacherContainer({ dict, initialData, type }: TeachersContainerProps) {
  const teacherEventBus = createEventBus<TeacherEvents>('teacher-scope');

  const { profile } = useProfileStore();
  const [viewReady, setViewReady] = useState(false);
  const router = useRouter();

  // Callback to handle the view-ready state
  const handleTeacherView = useCallback(() => {
    setViewReady(true);
  }, []);

  const handleRedirect = React.useCallback(
    (params: { url: string; isExternalPage: boolean }) => {
      if (params.isExternalPage) {
        window.open(params.url, '_blank', 'noopener,noreferrer');
        return;
      }
      router.push(params.url);
    },
    [router],
  );

  // Callback per gestire l'evento "teachers/doDetail"
  const handleDoDetail = useCallback(
    (data: { id: string; displayName: string }) => {
      // Trasforma displayName in una stringa formattata
      const url = routes.teacherDetails.makeUrl(data?.displayName, data?.id);
      console.log(`Navigating to teacher detail: ${url}`);
      router.push(url);
    },
    [router],
  );

  useEffect(() => {
    // Set up the listeners before emitting the event
    teacherEventBus.on(TeacherEventKeys.teachers.view, handleTeacherView);
    teacherEventBus.on(TeacherEventKeys.teacher.view, handleTeacherView);
    teacherEventBus.on(TeacherEventKeys.teachers.doDetail, handleDoDetail);
    teacherEventBus.on(TeacherEventKeys.redirects.to, handleRedirect);

    console.log('type', type);
    // Emit the event after the listeners are set up
    if (type === 'list') {
      teacherEventBus.emit(TeacherEventKeys.teachers.view, undefined);
    } else if (type === 'detail') {
      teacherEventBus.emit(TeacherEventKeys.teacher.view, undefined);
    }

    // Cleanup to avoid memory leaks
    return () => {
      teacherEventBus.off(TeacherEventKeys.teachers.view, handleTeacherView);
      teacherEventBus.off(TeacherEventKeys.teacher.view, handleTeacherView);
      teacherEventBus.off(TeacherEventKeys.teachers.doDetail, handleDoDetail);
      teacherEventBus.off(TeacherEventKeys.redirects.to, handleRedirect);
    };
  }, [teacherEventBus, handleDoDetail, handleTeacherView, handleTeacherView]);

  if (!viewReady) {
    if (type === 'list') {
      // Skeleton for the list view
      return (
        <div>
          <div className="mb-10 flex flex-col items-center gap-y-4">
            <Skeleton variant="text" height={60} width={350} />
            <Skeleton variant="text" height={30} width={300} />
          </div>
          <Grid container spacing={2}>
            {Array.from(Array(8)).map((_, i) => (
              <Grid key={`skeleton-${i}`} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                <Card
                  className="p-4 bg-neutral-main/10 flex flex-col justify-start w-full h-full max-w-[318px] mx-auto md:max-w-full"
                  sx={{ boxShadow: 'none' }}
                >
                  <Skeleton
                    sx={{ height: 190 }}
                    animation="wave"
                    variant="rectangular"
                    className="rounded-xl pb-0"
                  />
                  <div className="pt-4 space-y-2">
                    <Skeleton variant="text" height={28} width="70%" />
                    <Skeleton variant="text" height={24} width={90} />
                  </div>
                </Card>
              </Grid>
            ))}
          </Grid>
        </div>
      );
    }

    if (type === 'detail') {
      // Skeleton for the detail view
      return (
        <div className="w-full flex flex-col gap-y-">
          <Skeleton
            variant="text"
            width="40%"
            height={60}
            className="mb-2 md:mb-6 bg-neutral-main/20"
          />
          <Grid container className="w-full" spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card
                className="bg-neutral-main/10 rounded-2xl h-[300px] lg:h-[500px]"
                sx={{ boxShadow: 'none' }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} display={'flex'} flexDirection={'column'} rowGap={2}>
              <Card
                className="bg-neutral-main/10 rounded-2xl p-6 h-full"
                sx={{ boxShadow: 'none' }}
              >
                <Skeleton variant="text" className="w-full" />
                <Skeleton variant="text" className="w-full" />
                <Skeleton variant="text" className="w-1/2" />
              </Card>
              <div className="flex gap-4">
                <Card
                  className="bg-neutral-main/10 rounded-2xl p-6 h-[100px] w-full"
                  sx={{ boxShadow: 'none' }}
                >
                  <Skeleton variant="text" className="w-full" />
                  <Skeleton variant="text" className="w-1/2" />
                </Card>
                <Card
                  className="bg-neutral-main/10 rounded-2xl p-6 h-[100px] w-full md:w-1/3"
                  sx={{ boxShadow: 'none' }}
                >
                  <Skeleton variant="text" className="w-full" />
                  <Skeleton variant="text" className="w-1/2" />
                </Card>
              </div>
            </Grid>
          </Grid>
        </div>
      );
    }
  }

  return (
    <>
      <TeacherScreen
        eventBus={teacherEventBus}
        initialData={initialData}
        dict={dict}
        isSubscriber={!!profile?.subscriber}
      />
    </>
  );
}

export default TeacherContainer;
